import React from 'react';
import { XCircle } from 'lucide-react';
import { useAuth } from '@contexts/AuthContext';
import Button from '@components/common/Button';
import ConfirmDialog from '@components/common/ConfirmDialog';
import Tooltip from '@components/common/Tooltip';
import { ACTIVE_PUBLISHED_STATUSES, type Assessment } from '@app-types';
import { canUserCancelAssessment } from '@utils/permissionUtils';
import { getApiErrorMessage } from '@utils/apiError';
import { showToast } from '@utils/toast';
import { useCancelAssessment } from '@hooks/useCancelAssessment';
import CancelAssessmentDialog from './CancelAssessmentDialog';
import styles from './CancelAssessmentAction.module.css';

export const CANCEL_PUBLISHED_FORBIDDEN_MESSAGE = 'Only a Program Lead or Admin can cancel a published assessment.';

interface CancelAssessmentActionProps {
  assessment: Assessment;
}

/**
 * The "Cancel Assessment" header action. Decides whether the button is hidden,
 * blocked (published assessment, Assessor only) or enabled, and which dialog it
 * opens: the reason dialog for a published assessment, the plain confirmation
 * before publication.
 */
const CancelAssessmentAction: React.FC<CancelAssessmentActionProps> = ({ assessment }) => {
  const { user } = useAuth();
  const cancelMutation = useCancelAssessment(assessment.assessment_id);
  const [showConfirm, setShowConfirm] = React.useState(false);
  const [showReasonDialog, setShowReasonDialog] = React.useState(false);
  const [serverError, setServerError] = React.useState<string | null>(null);
  const descriptionId = React.useId();

  const access = canUserCancelAssessment(user, assessment.assessment_status);
  if (access === 'hidden') return null;

  const isPublished = ACTIVE_PUBLISHED_STATUSES.includes(assessment.assessment_status);

  if (access === 'blocked') {
    return (
      <Tooltip content={CANCEL_PUBLISHED_FORBIDDEN_MESSAGE} dataTestId="cancel-assessment" position="bottom">
        <Button
          variant="danger"
          aria-disabled="true"
          aria-describedby={descriptionId}
          className={styles.blockedButton}
          onClick={(e) => e.preventDefault()}
          dataTestId="cancel-assessment-button"
        >
          <XCircle size={16} />
          Cancel Assessment
        </Button>
        <span id={descriptionId} className={styles.srOnly}>
          {CANCEL_PUBLISHED_FORBIDDEN_MESSAGE}
        </span>
      </Tooltip>
    );
  }

  const handleOpen = () => {
    if (isPublished) {
      setServerError(null);
      setShowReasonDialog(true);
    } else {
      setShowConfirm(true);
    }
  };

  const handleConfirmPlain = () => {
    setShowConfirm(false);
    cancelMutation.mutate(undefined, {
      onError: (error) => showToast.error(getApiErrorMessage(error, 'Failed to cancel assessment'))
    });
  };

  const handleConfirmReason = (reason: string) => {
    setServerError(null);
    cancelMutation.mutate(reason, {
      onSuccess: () => setShowReasonDialog(false),
      onError: (error) => setServerError(getApiErrorMessage(error, 'Failed to cancel assessment'))
    });
  };

  return (
    <>
      <Button
        variant="danger"
        onClick={handleOpen}
        disabled={cancelMutation.isPending}
        dataTestId="cancel-assessment-button"
      >
        <XCircle size={16} />
        {cancelMutation.isPending ? 'Cancelling...' : 'Cancel Assessment'}
      </Button>

      <ConfirmDialog
        isOpen={showConfirm}
        title="Cancel Assessment?"
        message="Are you sure you want to cancel this assessment? This action cannot be undone. The assessment will be moved to Cancelled status."
        confirmText="Cancel Assessment"
        cancelText="Go Back"
        confirmVariant="danger"
        onConfirm={handleConfirmPlain}
        onCancel={() => setShowConfirm(false)}
        dataTestId="cancel-assessment-dialog"
      />

      {showReasonDialog && (
        <CancelAssessmentDialog
          status={assessment.assessment_status}
          isPending={cancelMutation.isPending}
          serverError={serverError}
          onConfirm={handleConfirmReason}
          onClose={() => setShowReasonDialog(false)}
        />
      )}
    </>
  );
};

export default CancelAssessmentAction;
